import { Avatar, Box, Chip, ListItemIcon, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { useSnackbar } from "notistack";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import { formatCompact, formatUsername } from "../plans/influencerFormat";
import { formatCommentTimestamp, linkifyText } from "../brief/briefUiUtils";
import { getPlatform } from "../plans/platforms";

// Shared display pieces for the plan approval screens (board list, one plan's
// board, public review page) of both kinds — Script and Video Approval. Keys
// mirror the enums in pluginbackendnode/models/approval.js.

export const BRAND_GRADIENT = "linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)";

export const STATUS_META = {
  not_started: { label: "Not started", bg: "#F3F4F6", color: "#4B5563", dot: "#9CA3AF" },
  in_review: { label: "In review", bg: "#DBEAFE", color: "#1E40AF", dot: "#3B82F6" },
  changes_requested: { label: "Changes requested", bg: "#FEF3C7", color: "#92400E", dot: "#F59E0B" },
  approved: { label: "Approved", bg: "#D1FAE5", color: "#065F46", dot: "#10B981" },
};
export const STATUS_ORDER = ["not_started", "in_review", "changes_requested", "approved"];

export const PARTY_LABELS = { creator: "Creator", brand: "Brand", team: "Team" };
export const ACTOR_COLORS = { team: "#160647", brand: "#7C3AED", creator: "#FF5200" };

export const errorMessage = (error, fallback = "Something went wrong. Try again.") =>
  error?.response?.data?.message || fallback;

export const copyText = async (text, enqueueSnackbar, label = "Link") => {
  try {
    await navigator.clipboard.writeText(text);
    enqueueSnackbar(`${label} copied`, { variant: "success" });
  } catch {
    enqueueSnackbar(`Couldn't copy — ${text}`, { variant: "warning" });
  }
};

// Copy / open / replace for one share link — an approval board's brand link
// (header), each creator's link (row menu) and the Plans "Share with brand" link.
export const ShareLinkMenu = ({ anchorEl, onClose, url, label, onRotate, extraItems = null }) => {
  const { enqueueSnackbar } = useSnackbar();
  return (
    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={onClose} onClick={(e) => e.stopPropagation()}>
      <MenuItem onClick={() => { copyText(url, enqueueSnackbar, label); onClose(); }}>
        <ListItemIcon><LinkIcon fontSize="small" /></ListItemIcon>Copy {label.toLowerCase()}
      </MenuItem>
      <MenuItem onClick={() => { window.open(url, "_blank", "noopener,noreferrer"); onClose(); }}>
        <ListItemIcon><OpenInNewIcon fontSize="small" /></ListItemIcon>Open {label.toLowerCase()}
      </MenuItem>
      <MenuItem onClick={() => { onRotate(); onClose(); }}>
        <ListItemIcon><AutorenewIcon fontSize="small" /></ListItemIcon>Replace link (old one stops working)
      </MenuItem>
      {extraItems}
    </Menu>
  );
};

export const StatusChip = ({ status, size = "small" }) => {
  const meta = STATUS_META[status] || STATUS_META.not_started;
  return (
    <Chip
      size={size}
      label={meta.label}
      icon={<Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: meta.dot, ml: "8px !important" }} />}
      sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 600, fontSize: 12 }}
    />
  );
};

const DECISION_META = {
  approved: { Icon: CheckCircleRoundedIcon, color: "#10B981", text: "Approved" },
  changes_requested: { Icon: ErrorRoundedIcon, color: "#F59E0B", text: "Changes requested" },
  pending: { Icon: ScheduleRoundedIcon, color: "#9CA3AF", text: "Pending" },
};

// "Brand ✓" — who signed off, and when (tooltip).
export const ApprovalPill = ({ party, approval, showText = false }) => {
  const decision = approval?.decision || "pending";
  const { Icon, color, text } = DECISION_META[decision];
  const stale =
    decision === "pending" && approval?.lastDecision
      ? ` (${approval.lastDecision === "approved" ? "approved" : "asked for changes on"} v${approval.lastVersion})`
      : "";
  const who = approval?.by && decision !== "pending" ? ` by ${approval.by.name}, ${formatCommentTimestamp(approval.at)}` : "";
  return (
    <Tooltip title={`${PARTY_LABELS[party]}: ${text}${who}${stale}`} arrow>
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.4 }}>
        <Icon sx={{ fontSize: 15, color }} />
        <Typography component="span" sx={{ fontSize: 12, color: "#4B5563", fontWeight: 500 }}>
          {PARTY_LABELS[party]}
          {showText && `: ${text}`}
        </Typography>
      </Box>
    </Tooltip>
  );
};

export const ApprovalPills = ({ approvals, showText }) => (
  <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap" }}>
    {/* Brand only — mirrors APPROVERS in pluginbackendnode/models/approval.js. */}
    <ApprovalPill party="brand" approval={approvals?.brand} showText={showText} />
  </Box>
);

// Username + followers is all the creator info these boards show.
export const CreatorCell = ({ item, subtitle = true }) => {
  const handle = formatUsername(item.username);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
      <Avatar src={item.avatar || undefined} sx={{ width: 34, height: 34, fontSize: 14 }}>
        {(handle || "?").charAt(0).toUpperCase()}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontWeight: 600, fontSize: 14 }}>@{handle}</Typography>
        {subtitle && (
          <Typography noWrap sx={{ fontSize: 12, color: "#6B7280" }}>
            {formatCompact(item.followers)} {(getPlatform(item.platform)?.followersLabel ?? "Followers").toLowerCase()}
            {item.removedFromPlan && " · removed from plan"}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export const LastActivity = ({ at, by }) => (
  <Box>
    <Typography sx={{ fontSize: 13 }}>{formatCommentTimestamp(at)}</Typography>
    {by && (
      <Typography sx={{ fontSize: 12, color: "#6B7280" }}>
        {by.name} · {PARTY_LABELS[by.type]}
      </Typography>
    )}
  </Box>
);

export const LinkifiedText = ({ text }) =>
  linkifyText(text || "").map((piece, i) =>
    piece.url ? (
      <a key={i} href={piece.url} target="_blank" rel="noopener noreferrer" style={{ color: "#FF5200", wordBreak: "break-all" }}>
        {piece.url}
      </a>
    ) : (
      <span key={i}>{piece.text}</span>
    )
  );

export const formatBytes = (bytes) => {
  if (!bytes) return "0 MB";
  const mb = bytes / (1024 * 1024);
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
};

export const pageSx = {
  p: { xs: 2, sm: 3, md: 4 },
  marginLeft: "65px",
  marginTop: 8.2,
  minHeight: "calc(100vh - 66px)",
  background: "linear-gradient(145deg, #EDF1FF 0%, #F2EEFF 38%, #FFF0ED 100%)",
};

export const cardSx = {
  bgcolor: "#fff",
  borderRadius: "16px",
  border: "1px solid rgba(0,0,0,0.06)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
};

export const primaryButtonSx = {
  background: BRAND_GRADIENT,
  boxShadow: "0 4px 14px rgba(255,82,0,0.3)",
  "&:hover": { background: BRAND_GRADIENT, opacity: 0.9 },
  "&.Mui-disabled": { background: "#F3F4F6", color: "#9CA3AF", boxShadow: "none" },
  textTransform: "none",
  fontWeight: 700,
  borderRadius: "10px",
  px: 2.5,
};

export const secondaryButtonSx = {
  textTransform: "none",
  fontWeight: 600,
  borderRadius: "10px",
  color: "#374151",
  borderColor: "#E5E7EB",
  bgcolor: "#fff",
  "&:hover": { borderColor: "#D1D5DB", bgcolor: "#F9FAFB" },
};

export const headCellSx = {
  color: "text.secondary",
  fontWeight: 600,
  py: 1.5,
  fontSize: "0.85rem",
  whiteSpace: "nowrap",
  bgcolor: "background.paper",
  borderColor: "grey.100",
};
export const bodyCellSx = { borderColor: "grey.100", py: 1.25 };
